import { FC, useContext, useEffect, useRef, useState } from "react";
import { IQrCode, IQrCodeCreate, RequiredFields } from "../../models";
import { TbCurrentLocation, TbCurrentLocationOff } from "react-icons/tb";
import { Circles } from "react-loader-spinner";
import Context from "../../../../common/components/utils/context";
import { notifcation } from "../../../../common/components/utils/notifications";
import { IModal } from "../../../../common/components/modal/modal";
import { environment } from "../../../../common/env";
import { Utils } from "../../../../common/helpers/utils";

export const CodeEdit: FC<IEditCode> = (data) => {
  const context = useContext(Context);

  const [isDisabled, setDisabled] = useState(false);
  const [create, setCreate] = useState<boolean>(true);
  const [fields, setFields] = useState<IQrCode>(data ?? ({} as IQrCode));
  const [location, setLocation] = useState<boolean>(false);
  const [canlocate, setCanlocate] = useState<boolean>(false);
  const [valid, setValid] = useState<boolean>(false);

  useEffect(() => {
    data.id && setCreate(false);
    navigator.geolocation && setCanlocate(true);
    console.log(data);
    if (!data.id) {
      setFields({
        ...fields,
        description: "",
        latitude: data?.latitude ?? 0,
        longitude: data?.longitude ?? 0,
        altitude: data?.altitude ?? 0,
        heading: 0,
      } as IQrCode);
    }
  }, [data]);

  useEffect(() => {
    setValid(validate(fields));
  }, [fields]);

  useEffect(() => {
    locate();
  }, [location]);

  const validate = (input: any): input is RequiredFields<IQrCodeCreate> => {
    const schema: Record<keyof RequiredFields<IQrCodeCreate>, string> = {
      description: "string",
      latitude: "string",
      longitude: "string",
      altitude: "string",
      heading: "string",
    };
    const missing = Object.keys(schema)
      .filter((key) => input[key] === "")
      .map((key) => key as keyof RequiredFields<IQrCodeCreate>);

    return missing.length === 0;
  };

  const reset = () => {
    setFields(data);
    setLocation(false);
    context.setModal({ ...({} as IModal) });
  };

  const cancel = async () => {
    if (fields !== data) {
      const confirm = await notifcation({
        title: "Cancel",
        message: "Unsaved changes will be lost.",
        cancel: "Go Back",
        confirm: "Confirm Cancel",
      });
      if (confirm) {
        reset();
      }
    } else {
      reset();
    }
  };

  const save = (e: any) => {
    e.preventDefault();
    data.save?.(fields);
    reset();
  };

  const locate = () => {
    if (navigator.geolocation && location) {
      setDisabled(true);
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const altitude = await Utils.getElevation(
            position.coords.latitude,
            position.coords.longitude
          );

          setFields({
            ...fields,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            altitude: altitude,
          });
          setDisabled(false);
        },
        (error) => {
          setDisabled(false);
          console.error(`Error getting location: ${error.message}`);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    }
  };

  return (
    <div className="bg-white rounded-lg mx-auto px-5 py-8">
      <div className="grid grid-flow-col space-x-4">
        <h2 className="text-xl font-medium">{create ? "Create" : "Update"}</h2>
        <div className="flex justify-end">
          <div className="inline-flex items-center overflow-hidden rounded-lg border border-stroke dark:border-dark-3">
            <button
              disabled={isDisabled}
              hidden={!canlocate}
              className={`border-r px-3 py-2 last-of-type:border-r-0 ${
                location ? "bg-gray-100 shadow-inner" : "hover:bg-gray-50"
              }`}
              onClick={() => setLocation(!location)}
            >
              {isDisabled ? (
                <Circles height="20" width="20" color="#2DE1FC" />
              ) : location ? (
                <TbCurrentLocation className="text-logo" size={20} />
              ) : (
                <TbCurrentLocationOff size={20} />
              )}
            </button>
          </div>
        </div>
      </div>
      <form
        onSubmit={save}
        className="flex flex-col space-y-5 justify-center items-center"
      >
        <fieldset className="w-full">
          <label htmlFor="description" className="text-gray-500 text-sm">
            Description*
          </label>
          <input
            type="text"
            className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
            name="description"
            value={fields.description}
            onChange={(e) =>
              setFields({ ...fields, description: e.target.value })
            }
            required
          />
        </fieldset>
        <fieldset className="w-full">
          <label htmlFor="latitude" className="text-gray-500 text-sm">
            Latitude*
          </label>
          <input
            type="number"
            className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
            name="latitude"
            value={fields?.latitude}
            onChange={(e) =>
              setFields({ ...fields, latitude: e.target.value as any })
            }
            required
          />
        </fieldset>
        <fieldset className="w-full">
          <label htmlFor="longitude" className="text-gray-500 text-sm">
            Longitude*
          </label>
          <input
            type="number"
            className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
            name="longitude"
            value={fields?.longitude}
            onChange={(e) =>
              setFields({ ...fields, longitude: e.target.value as any })
            }
            required
          />
        </fieldset>
        <fieldset className="w-full">
          <label htmlFor="altitude" className="text-gray-500 text-sm">
            Altitude*
          </label>
          <input
            type="number"
            className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
            name="altitude"
            value={fields?.altitude}
            onChange={(e) =>
              setFields({ ...fields, altitude: e.target.value as any })
            }
            required
          />
        </fieldset>
        <fieldset className="w-full">
          <label htmlFor="heading" className="text-gray-500 text-sm">
            Heading*
          </label>
          <input
            type="number"
            className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
            name="heading"
            value={fields.heading}
            onChange={(e) =>
              setFields({ ...fields, heading: e.target.value as any })
            }
            required
          />
        </fieldset>
        <div className="grid grid-flow-col justify-end space-x-4">
          <button
            type="button"
            onClick={cancel}
            className="transition-all rounded-lg text-gray-800 text-sm tracking-wider font-medium outline-none bg-gray-100 hover:bg-gray-200 px-10 py-2"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!valid}
            className={`transition-all rounded-lg ${
              !valid ? "opacity-25" : "hover:text-white text-gray-800"
            } text-sm tracking-wider font-medium outline-none bg-text-link px-10 py-2`}
          >
            {create ? "Create" : "Update"}
          </button>
        </div>
      </form>
    </div>
  );
};

export interface IEditCode extends IQrCode {
  save: (item: IQrCode) => void;
}
